<script lang="ts">
    import { animate } from '@motionone/dom';
    import { inView } from '@motionone/dom';
    import ellipseBlue from '/src/assets/ellipse-blue.svg';
    import ellipseRed from '/src/assets/ellipse-red.svg';
    import { onMount } from 'svelte';
    import { reveal } from 'svelte-reveal';
    import CVSection from '../CVSection.svelte';
    import VideoPlayer from '$lib/components/VideoPlayer.svelte';
    import ScrambleIn from '$lib/components/ScrambleIn.svelte';
    import Quote from '$lib/components/Quote.svelte';

    // Place these at the top-level, outside any function or onMount
    const categoryLogos: { [key: string]: string[] } = {
      'Design to Inform': [
        '/logos/bmg.png',
        '/logos/DB.png',
        '/logos/AOK.png',
        '/logos/british-museum.png',
      ],
      'Design to Discover': [
        '/logos/google.png',
        '/logos/Futurium.png',
        '/logos/nio.png',
        '/logos/VW_Future.png',
        '/logos/vw.png',
      ]
      // 'Teaching & Leadership' does not need logos
    };

    let logoIndices: { [key: string]: number } = {
      'Design to Inform': 0,
      'Design to Discover': 0,
      'Teaching & Leadership': 0
    };

    // Show three logos at a time: previous, current, next
    let visibleLogos: { [key: string]: string[] } = {
      'Design to Inform': [
        categoryLogos['Design to Inform'][categoryLogos['Design to Inform'].length - 1],
        categoryLogos['Design to Inform'][0],
        categoryLogos['Design to Inform'][1]
      ],
      'Design to Discover': [
        categoryLogos['Design to Discover'][categoryLogos['Design to Discover'].length - 1],
        categoryLogos['Design to Discover'][0],
        categoryLogos['Design to Discover'][1]
      ]
    };

    // Flatten all logo paths into a single array
    const allLogos: string[] = Object.values(categoryLogos).flat();

    interface Project {
      title: string;
      description: string;
      category: string;
      context: string;
      image: string;
      link: string;
      isVideo?: boolean;
      isVisible?: boolean;
    }

    interface CategoryProjects {
      [key: string]: Project[];
    }

    let rotatingEl: SVGElement;
    
    let redCircle: HTMLElement;
    let blueCircle: HTMLElement;
  
    let aboutText: HTMLElement;
    let workText: HTMLElement;
  
    let hoveredImage: string | null = null;
  
    const projects: Project[] = [
      
    {
        title: "Made to Measure",
        description: "An interactive data visualization tool for exploring and understanding complex measurements and metrics.",
        category: "Design to Inform",
        context: "Experience Design",
        image: "https://vz-6b226b53-3c8.b-cdn.net/4a6745e8-0954-44ec-8649-46f95639c69d/playlist.m3u8",
        link: "/projects/made-to-measure",
        isVideo: true
      },
      {
        title: "AOK Design System",
        description: "A comprehensive design system for Germany's largest health insurance provider, ensuring consistent and accessible digital experiences.",
        category: "Design to Inform",
        context: "Healthcare",
        image: "https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fqcare.fb6af2a9.png&w=3840&q=80",
        link: "/projects/aok-design-system"
      },
      {
        title: "Blanq",
        description: "A digital platform for exploring and understanding wine data, making complex information accessible and engaging.",
        category: "Design to Inform",
        context: "Fintech",
        image: "https://vz-6b226b53-3c8.b-cdn.net/205dff08-fa0d-4cb3-aa08-a8b441c4d1e8/playlist.m3u8",
        link: "/projects/blanq",
        isVideo: true
      },
      {
        title: "Berlin Budget Explorer",
        description: "An interactive tool for exploring and understanding Berlin's public budget, making fiscal data accessible to all.",
        category: "Design to Inform",
        context: "Civic Tech",
        image: "/projects/berlin-budget/Haushaltsk_rzungen Berlin 2025.jpeg",
        link: "/projects/berlin-budget-explorer",
        isVisible: true
      },
      {
        title: "Pandemieradar",
        description: "A real-time visualization of COVID-19 data across Germany, providing insights into infection rates and trends.",
        category: "Design to Inform",
        context: "Healthcare",
        image: "https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fphone.b152c45e.jpg&w=3840&q=80",
        link: "/projects/pandemieradar"
      },

      {
        title: "DB Peak Spotting",
        description: "A data-driven platform for analyzing and visualizing peak travel times and passenger flows in the German rail network.",
        category: "Design to Inform",
        context: "Transportation",
        image: "https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fpeakspotting.36ef0e4e.jpg&w=3840&q=80",
        link: "/projects/db-peak-spotting",
        isVisible: true
      },


      {
        title: "Project Soli",
        description: "Developing gesture recognition technology for natural, touchless interactions with digital devices.",
        category: "Design to Discover",
        context: "Emerging Tech",
        image: "https://vz-6b226b53-3c8.b-cdn.net/375486f6-d90f-4c34-9e68-171ba5817c53/playlist.m3u8",
        link: "/projects/project-soli",
        isVideo: true
      },
      {
        title: "Self-Driving Car Interactions",
        description: "Designing intuitive interactions for autonomous vehicles in urban environments.",
        category: "Design to Discover",
        context: "Transportation",
        image: "https://vz-6b226b53-3c8.b-cdn.net/defc863b-b8f3-4a50-991d-77b3bd98ea63/playlist.m3u8",
        link: "/projects/self-driving-car",
        isVideo: true,
        isVisible: true
      },
  
      {
        title: "FactSnap",
        description: "An AI-powered tool for analyzing and verifying information in real-time.",
        category: "Design to Discover",
        context: "Emerging Tech",
        image: "https://framerusercontent.com/images/cRDyiqgVq3dtMGZBtBi4FE1E3w.jpg?scale-down-to=2048",
        link: "/projects/fact-snap",
        isVisible: false,
      },
      
      {
        title: "The Known Unknowns",
        description: "Exploring uncertainty and ambiguity in design through experimental prototypes and research.",
        category: "Design to Discover",
        context: "Emerging Tech",
        image: "https://vz-6b226b53-3c8.b-cdn.net/e67026e0-5fa8-40bb-996e-855557b6a2d4/playlist.m3u8",
        link: "/projects/known-unknowns",
        isVideo: true,
        isVisible: false
      },
      {
        title: "Do You Like Working",
        description: "An interactive installation exploring the relationship between work and personal identity through immersive experiences.",
        category: "Design to Discover",
        context: "Emerging Tech",
        image: "/projects/do-you-like-working/exhibition-overview.jpg",
        link: "/projects/do-you-like-working"
      },
      {
        title: "The Gestalt of Digital Identity",
        description: "Investigating how digital identities are formed, perceived, and evolve in the modern digital landscape.",
        category: "Design to Discover",
        context: "Emerging Tech",
        image: "https://vz-6b226b53-3c8.b-cdn.net/28bf4043-d82d-4e43-a49f-011ead7fab5a/playlist.m3u8",
        link: "/projects/gestalt-digital-identity",
        isVideo: true,
        isVisible: false
      },
      {
        title: "Design for Decisions",
        description: "A case study on designing systems for decision-making in complex scenarios.",
        category: "Design to Discover",
        context: "Leadership & Strategy",
        image: "projects/design-for-decisions/dfd-cover-hand.jpg",
        link: "/projects/design-for-decisions"
      },
      {
        title: "Is This Thing On?",
        description: "I have been teaching Information and Interface Design at Germanys leading art & design schools.",
        category: "Design to Discover",
        context: "Emerging Tech",
        image: "projects/itto/itto-words-of-a-middle-man.jpg",
        link: "/projects/is-this-thing-on",
        isVisible: true
      },
      {
        title: "Framing Art Science Technology",
        description: "Developing a postgraduate program bridging art, science, and emerging technologies.",
        category: "Design to Discover",
        context: "Education",
        image: "projects/fast/fast-key.png",
        link: "/projects/framing-art-science-technology",
        isVisible: false
      },

    ];

    // Group projects by category and filter out invisible projects
    const groupedProjects: CategoryProjects = projects
      .filter(project => project.isVisible !== false) // Only show projects where isVisible is not false
      .reduce((acc, project) => {
        if (!acc[project.category]) {
          acc[project.category] = [];
        }
        acc[project.category].push(project);
        return acc;
      }, {} as CategoryProjects);

    let leftWord: HTMLElement;
    let rightWord: HTMLElement;
    let rotationCount = 0;
    const words = ['Creative', 'Lead', 'Design', 'Futures', 'UX', 'AI', 'Emerging', 'Technologies', 'Data', 'Visualization'];
    let currentWordIndex = 0;
  
    let lastScrollY = 0;
  
    // Location data variables
    let location = {
      city: '',
      country: ''
    };
    let locationLoading = true;
    let locationError = false;
    
    // Function to get location data
    async function getLocationData() {
      try {
        // Get the user's location from their IP
        const ipResponse = await fetch('https://ipapi.co/json/');
        
        if (!ipResponse.ok) {
          throw new Error(`IP API responded with status: ${ipResponse.status}`);
        }
        
        const ipData = await ipResponse.json();
        
        // Update the location object
        location = {
          city: ipData.city || 'your city',
          country: ipData.country_name || 'your country'
        };
        
        locationLoading = false;
      } catch (error) {
        console.error('Error fetching location data:', error);
        locationError = true;
        locationLoading = false;
      }
    }
  
  
    function updateWords() {
      if (!leftWord || !rightWord) return;
      
      leftWord.classList.add('fade-out');
      rightWord.classList.add('fade-out');
      
      setTimeout(() => {
        leftWord.textContent = words[currentWordIndex];
        rightWord.textContent = words[currentWordIndex + 1];
        
        leftWord.classList.remove('fade-out');
        rightWord.classList.remove('fade-out');
      }, 300);
    }
  
    // Add these variables for idle animation
    let isScrolling = false;
    let scrollTimeout: number;
    let idleRotation = 0;
    let animationFrame: number | undefined;
    
    function animateCircles() {
      if (!isScrolling && redCircle && blueCircle) {
        idleRotation += 0.05; // Very slow rotation speed
        redCircle.style.transform = `rotate(${-idleRotation}deg)`;
        blueCircle.style.transform = `rotate(${idleRotation}deg)`;
        animationFrame = requestAnimationFrame(animateCircles);
      }
    }
    
    let workMarquee: HTMLElement;
    let workWordWidth = 0;

    function updateWorkMarquee(scrollY: number) {
      if (!workMarquee || !workWordWidth) return;
      const factor = 0.5; // adjust for speed
      const totalWidth = workMarquee.scrollWidth / 2;
      const offset = (scrollY * factor) % totalWidth;
      workMarquee.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    function handleScroll() {
      if (!redCircle || !blueCircle || !aboutText || !workMarquee) return;
      
      // Cancel any ongoing idle animation
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
      
      isScrolling = true;
      
      // Use requestAnimationFrame for smooth animation
      requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        const scrollingDown = scrolled > lastScrollY;
        
        // Throttle rotation updates
        const rotationMultiplier = window.innerWidth < 768 ? 0.8 : 0.4;
        const rotation = scrolled * rotationMultiplier;
        const movement = scrolled * 0.5;
        
        // More frequent word changes on mobile (every 45 degrees instead of 90)
        const rotationThreshold = window.innerWidth < 768 ? 45 : 90;
        const quarterRotations = Math.floor(Math.abs(rotation) / rotationThreshold);
        
        if (quarterRotations !== rotationCount) {
          rotationCount = quarterRotations;
          if (scrollingDown) {
            currentWordIndex = (currentWordIndex + (window.innerWidth < 768 ? 1 : 2)) % words.length;
          } else {
            currentWordIndex = (currentWordIndex - (window.innerWidth < 768 ? 1 : 2) + words.length) % words.length;
          }
          updateWords();
        }
        
        // Use transform3d for hardware acceleration
        redCircle.style.transform = `translate3d(0, 0, 0) rotate(${-rotation}deg)`;
        blueCircle.style.transform = `translate3d(0, 0, 0) rotate(${rotation}deg)`;
        aboutText.style.transform = `translate3d(-${scrolled * 0.5}px, 0, 0)`;
        updateWorkMarquee(scrolled);
        
        lastScrollY = scrolled;
      });
      
      // Clear the timeout if it exists
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Set a timeout to stop the scrolling state after 1 second
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
        // Save the current rotation as the starting point for idle animation
        const currentRotation = window.scrollY * (window.innerWidth < 768 ? 0.8 : 0.4);
        idleRotation = currentRotation % 360;
        // Start the idle animation
        animationFrame = requestAnimationFrame(animateCircles);
      }, 1000) as unknown as number;
      
      // Show back to top button after scrolling down 500px
      const currentScroll = window.scrollY;
      showBackToTop = currentScroll > 500;
    }
  
    // Add throttling to scroll event
    let ticking = false;
    
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }
  
    // Bind to scroll event when the component mounts
    onMount(() => {
      window.addEventListener('scroll', onScroll, { passive: true });
      
      // Add animations for elements when they come into view
      inView('.animate-on-view', ({ target }) => {
        if (!target.classList.contains('in-view')) {
          animate(
            target,
            {
              opacity: 1,
              transform: 'translate3d(0, 0, 0) translateY(0)',
              filter: 'blur(0px)'
            },
            {
              duration: 0.8,
              easing: [0.16, 1, 0.3, 1]
            }
          );
          target.classList.add('in-view');
        }
      }, { amount: 0.3 });
      
      // Get location data when component mounts
      getLocationData();
      
      // Start the idle animation by default
      setTimeout(() => {
        if (redCircle && blueCircle) {
          console.log("Starting idle animation");
          isScrolling = false;
          animationFrame = requestAnimationFrame(animateCircles);
        }
      }, 500);
      
      // Create intersection observer for CV section
      const cvObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            projectsInView = entry.isIntersecting;
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px'
        }
      );
  
      if (cvSection) {
        cvObserver.observe(cvSection);
      }
      
      // Measure the width of one 'Work' word after mount
      setTimeout(() => {
        const wordEl = workMarquee?.querySelector('.work-word');
        if (wordEl) {
          workWordWidth = (wordEl as HTMLElement).offsetWidth + parseInt(getComputedStyle(wordEl as HTMLElement).marginRight);
        }
      }, 100);
      
      // Animate bio content
      setTimeout(() => {
        if (bioContent) {
          animate(
            bioContent,
            { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
            { duration: 0.8, easing: [0.16, 1, 0.3, 1] }
          );
          bioContent.classList.add('in-view');
        }
      }, 0);
      
      // Animate JL heading (aboutText) after mount
      setTimeout(() => {
        if (aboutText) {
          animate(
            aboutText,
            { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
            { duration: 0.8, easing: [0.16, 1, 0.3, 1] }
          );
          aboutText.classList.add('in-view');
        }
      }, 0);
      
      // Cycle logos every 2 seconds
      const interval = setInterval(() => {
        for (const cat in logoIndices) {
          const arr = categoryLogos[cat];
          if (arr && arr.length) {
            logoIndices[cat] = (logoIndices[cat] + 1) % arr.length;
            // Compute previous, current, next indices
            const len = arr.length;
            const current = logoIndices[cat];
            const prev = (current - 1 + len) % len;
            const next = (current + 1) % len;
            visibleLogos[cat] = [arr[prev], arr[current], arr[next]];
          }
        }
        visibleLogos = { ...visibleLogos };
      }, 2000);
      
      return () => {
        window.removeEventListener('scroll', onScroll);
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
        cvObserver.disconnect();
        clearInterval(interval);
      };
    });
  
    // Add back to top functionality
    let showBackToTop = false;
    
    function scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  
    let projectsInView = false;
    let cvSection: HTMLElement;
  
    // Add minimized state for each category
    let minimizedCategories: { [key: string]: boolean } = {
      "Design to Inform": false, // closed
      "Design to Discover": false, // open
      "Teaching & Leadership":  false// open
    };
    
    function toggleCategory(category: string) {
      minimizedCategories[category] = !minimizedCategories[category];
    }

    let bioContent: HTMLElement;
</script>
    <!-- Ensure Default Export -->

  
  
  <!-- Main container -->
  <div class="min-h-screen">
    
    <!-- Content area (with padding to account for fixed header) -->
    <div class="pt-16 md:pt-24 lg:pt-28 xl:pt-32 2xl:pt-36">
      <!-- Animated circles (fixed position) -->
      <div class="fixed top-[20%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 animate fade">
        <div class="mt-40 sm:mt-40 gradient-circles mb-8 sm:mb-24">
          <!-- Left word -->
          <div 
            bind:this={leftWord}
            class="absolute -left-48 top-1/2 -translate-y-1/2 text-lg font-light word-transition text-blue-500 text-right blue-word"
          >
            Creative
          </div>
          <!-- Right word -->
          <div 
            bind:this={rightWord}
            class="absolute -right-48 top-1/2 -translate-y-1/2 text-lg font-light word-transition text-orange-500 text-right orange-word"
          >
            Lead
          </div>
          <!-- Animated circles -->
          <div class="relative mt-24 sm:mt-0">
            <img 
              bind:this={redCircle}
              src={ellipseRed} 
              alt="Red gradient circle" 
              class="circle circle-red" 
            />
            <img 
              bind:this={blueCircle}
              src={ellipseBlue} 
              alt="Blue gradient circle" 
              class="circle circle-blue" 
            />
          </div>
        </div>
      </div>
  
      <!-- About section -->
      <section class="container mx-auto px-2 md:px-4 max-w-5xl lg:max-w-6xl xl:max-w-[1600px] 2xl:max-w-[1920px]">
        <!-- JL heading and location -->
        <div class="mb-8 md:mb-16 overflow-hidden animate fade delay-1">
          <h2 
            bind:this={aboutText}
            class="text-[300px] pt-8 font-light text-black select-none whitespace-nowrap"
          >
            JL
          </h2>
  
  
        <!-- Bio content -->
        <div class="mt-32 sm:mt-16 relative z-10 delay-2 backdrop-blur-md bg-white/70 mix-blend-multiply" bind:this={bioContent}>
          <div class="max-w-xl p-4 sm:p-16 bg-[#FFF]">
            <div class="flex flex-col sm:block">
              <h2 class="text-base sm:text-lg md:text-xl leading-normal">
                Creative Lead & <span class="rotating-words"> 
                
                  <span class="word" style="--i: 1">AIxUX</span>
                  <span class="word" style="--i: 2">Information</span>
                  <span class="word" style="--i: 3">Product</span>
                </span>
              </h2>
              <h2 class="-mt-4 text-base sm:text-lg md:text-xl leading-normal">
                Designer.
              </h2>
            </div>
            <ScrambleIn text="Hello. My name is Jonas." className="text-xl font-light" />
            <p class="text-sm sm:text-base text-gray-700 leading-relaxed mt-4">
              I'm a Creative Lead with 13+ years of experience directing teams to build award-winning digital products, data-driven dashboards, and interactive experiences.
            </p>
            <p class="text-sm sm:text-base text-gray-700 leading-relaxed mt-4">
              I work at the intersection of technology, design, and strategy — aligning vision and execution to help people navigate complexity, spot weak signals, and make informed decisions.
            </p>
          </div>
        </div>
      </section>



      <!-- Projects section -->
      <section class="space-y-32">
          <!-- Work heading -->
          <div 
            bind:this={cvSection}
            class="container mx-auto px-2 md:px-4 max-w-5xl lg:max-w-6xl xl:max-w-[1600px] 2xl:max-w-[1920px] mt-32 md:mt-48 lg:mt-64 xl:mt-80 2xl:mt-96"
          >
            <div class="mb-24 overflow-hidden">
              <div class="work-marquee-container">
                <div class="work-marquee" bind:this={workMarquee}>
                  {#each Array(20) as _, i}
                    <span class="work-word">Work&nbsp;</span>
                  {/each}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Projects grid (full width background) -->
          <div class="projects-section w-full">
            <div class="container mx-auto px-2 md:px-4 max-w-5xl lg:max-w-6xl xl:max-w-[1600px] 2xl:max-w-[1920px]">
              <!-- Projects content -->
              <div class="max-w-7xl mx-auto">
                <!-- Group projects by category -->
                {#each Object.entries(groupedProjects) as [category, categoryProjects]}
                  <!-- Category Header -->
                  <div class="mb-8 border-t border-gray-200 w-full">
                    <div class="container mx-auto px-2 md:px-4 max-w-5xl lg:max-w-6xl xl:max-w-[1600px] 2xl:max-w-[1920px]">
                      <div class="flex flex-col w-full pt-8 pb-4">
                        <div class="flex items-center justify-between w-full">
                          <h2 class="text-gray-700 mt-6 w-full">
                            {category === "Design to Inform" ? "Information Interfaces & Products" : 
                             category === "Design to Discover" ? "Strategic Prototypes & Applied Futures" : 
                             "Teaching & Leadership"}
                          </h2>
                          <button 
                            class="text-gray-700 hover:text-gray-600 transition-colors flex items-center ml-4"
                            on:click={() => toggleCategory(category)}
                            aria-label="Toggle category"
                          >
                            <svg
                              class="w-6 h-6 transition-transform duration-300"
                              style="transform: rotate({minimizedCategories[category] ? '0deg' : '90deg'});"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              viewBox="0 0 24 24"
                            >
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                          </button>
                        </div>
                        <p class="text-base text-gray-500 mt-2">
                          {category === "Design to Inform"
                             ? "Case studies of my work as creative lead, including interactive data-driven dashboards, novel interacitve experiences and digital products."
                              : category === "Design to Discover"
                               ? "Strategic prototypes and applied futures built to spot weak signals and shape sustainable products with emerging technologies."
                               : "Leadership and design communication through creative direction, teaching, and strategic framing."}
                        </p>
                        <!-- Logo carousel for each category except Teaching & Leadership -->
                        {#if category !== 'Teaching & Leadership'}
                          <div class="flex flex-wrap items-center justify-start mt-4 min-h-[40px] gap-4 logo-carousel">
                            {#each categoryLogos[category] as logo, i}
                              <img
                                src={logo}
                                alt={(logo.split('/').pop() ?? '').replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')}
                                class="h-8 w-auto object-contain logo-img {i === logoIndices[category] ? 'logo-center' : 'logo-blur'}"
                                style="max-width: 120px;"
                              />
                            {/each}
                          </div>
                        
                        {/if}
                      </div>
                    </div>
                  </div>
                  <!-- Projects Grid -->
                  <div 
                    class="transition-all duration-300 ease-in-out overflow-hidden"
                    class:max-h-0={minimizedCategories[category]}
                    class:max-h-[2000px]={!minimizedCategories[category]}
                  >
                    <div class="w-full mt-4 mb-16">
                      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-32">
                        {#each categoryProjects as project}
                          <div class="group" use:reveal={{ y: 20 }}>
                            <a href={project.link} class="block">
                              <!-- Project Image -->
                              <div class="relative aspect-[16/9] mb-4 overflow-hidden">
                                {#if project.isVideo}
                                  <VideoPlayer
                                    src={project.image}
                                    poster="/QCare Qualitätsatlas.webp"
                                    autoplay={true}
                                    muted={true}
                                    loop={true}
                                    controls={false}
                                    className="w-full h-full"
                                  />
                                {:else}
                                  <img 
                                    src={project.image} 
                                    alt={project.title}
                                    loading="lazy"
                                    class="w-full h-full {project.title === 'AOK Design System' ? 'object-contain' : 'object-cover'} transition-transform duration-500 group-hover:scale-105"
                                    srcset="
                                      {project.image.replace('.webp', '-small.webp')} 400w,
                                      {project.image.replace('.webp', '-medium.webp')} 800w,
                                      {project.image} 1200w
                                    "
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                  />
                                {/if}
                              </div>
                              
                              <!-- Project Info -->
                              <div class="space-y-2">
                                <div class="flex items-center gap-2">
                                  <h4 class="text-lg font-medium group-hover:text-[#F96604] transition-colors">
                                    {project.title}
                                  </h4>
                                  <span class="context-tag">
                                    {project.context}
                                  </span>
                                </div>
                                <p class="text-small text-gray-500">
                                  {project.description}
                                </p>
                              </div>
                            </a>
                          </div>
                        {/each}
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            </div>
          </div>


              <!-- Quote section -->
              <div class="mt-32 sm:mt-16 relative z-10 delay-2 backdrop-blur-md bg-white/70 mix-blend-multiply">
        <Quote
          text="Having had the privilege to work alongside Jonas for an extended period, I can confidently attest to his immense talent, dedication, and unwavering commitment to pushing the boundaries of design and technology."
          author="Ivan Poupyrev, Head of Design, Google"
          size="base"
        />
      </div>
          <!-- CV Section Component -->
          <div bind:this={cvSection} class="mt-32 md:mt-48 lg:mt-64 xl:mt-80 2xl:mt-96 border-t border-gray-200">
            <CVSection />
          </div>
        </section>  


      <!-- Footer -->
      <footer class="w-full bg-[#212121] mt-32 pt-8 border-t border-white-800">
        <div class="container mx-auto px-4 md:px-8 max-w-7xl">
          <div class="grid grid-cols-12 gap-8 py-16">
            <!-- Contact -->
            <div class="col-span-12 md:col-span-4">
              <h3 class="text-lg mb-4 text-white">Contact</h3>
              <a href="mailto:hello@jonasloh.com" class="text-gray-400 hover:text-[#F96604] transition-colors">
                mail@jonasloh.com
              </a>
            </div>
            
            <!-- Social -->
            <div class="col-span-12 md:col-span-4">
              <h3 class="text-lg mb-4 text-white">Social</h3>
              <div class="space-y-2">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" class="block text-gray-400 hover:text-[#F96604] transition-colors">LinkedIn</a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" class="block text-gray-400 hover:text-[#F96604] transition-colors">Instagram</a>
              </div>
            </div>
            
            <!-- Legal -->
            <div class="col-span-12 md:col-span-4">
              <h3 class="text-lg text-white mb-4">Legal</h3>
              <div class="space-y-2">
                <a href="/imprint" class="block text-gray-400 hover:text-[#F96604] transition-colors">Imprint</a>
              </div>
            </div>
          </div>
          
          <!-- Copyright -->
          <div class="py-8 text-sm text-gray-500">
            © {new Date().getFullYear()} Jonas Loh. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  
    <!-- Back to Top Button -->
    {#if showBackToTop}
      <button
        on:click={scrollToTop}
        class="fixed bottom-8 right-8 z-[9999] bg-black text-white px-3 py-1 rounded-full text-xs opacity-90 shadow-lg hover:opacity-100 transition-opacity duration-300"
        aria-label="Back to top"
      >
        <span class="mr-1">↑</span>top
      </button>
    {/if}
  </div>



  <style>
    @font-face {
      font-family: 'Simple-Regular';
      src: url('/src/assets/fonts/Simple-Regular.woff') format('woff'),
           url('/src/assets/fonts/Simple-Regular.woff2') format('woff2');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  
  
    /* Apply Akkurat Mono to most text */
    body, div, h1, h2, h3, p, span, a {
      font-family: Simple-Regular, system-ui, sans-serif;
      /*font-family: Akkurat-Mono, monospace;*/
    }
  
    /* Use Simple for large display text */
    .text-\[300px\] {
      font-family: Simple-Regular, system-ui, sans-serif;
    }
  
    .project-title {
      /*font-family: Akkurat-Mono, monospace;*/
      font-family: Simple-Regular, system-ui, sans-serif;
    }
  
    /* Add rotation animation */
    .rotating {
      animation: rotate 6s linear infinite;
    }
  
    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  
    .gradient-circles {
      width: 200px;  /* Smaller width for mobile */
      height: 140px; /* Smaller height for mobile */
      position: relative;
      margin-left: 0;  /* Remove margin on mobile */
  
      @media (min-width: 768px) {
        width: 333px;
        height: 232px;
        margin-left: 16rem;
      }
    }
  
    .circle {
      position: absolute;
      width: 140px;  /* Smaller circle size for mobile */
      height: 140px;
      mix-blend-mode: multiply;
      transition: none; /* Remove transition to prevent conflicts with animation */
  
      @media (min-width: 768px) {
        width: 232px;
        height: 232px;
      }
    }
  
    .circle-red {
      left: 0;
      z-index: 1;
    }
  
    .circle-blue {
      left: 70px;   /* Adjusted overlap for mobile */
      top: 70px;    /* Adjusted overlap for mobile */
      z-index: 0;
  
      @media (min-width: 768px) {
        left: 116px;
        top: 116px;
      }
    }
  
    .word-transition {
      transition: opacity 0.3s ease-in-out;
      opacity: 1;
      z-index: 2;
    }
  
    .fade-out {
      opacity: 0;
    }
  
    .blue-word {
      font-family: Simple-Regular, system-ui, sans-serif;
      opacity: 1;
      background-color: #fff;
      width: 180px;
      display: none;

      @media (min-width: 768px) {
        display: block;
      }
    }
  
    .orange-word {
      font-family: Simple-Regular, system-ui, sans-serif;
      opacity: 1;
      background-color: #fff;
      width: 180px;
      text-align: left;
      top: 225px;
      display: none;

      @media (min-width: 768px) {
        display: block;
      }
    }
  
    .grid {
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 2rem;
    }
  
    .splash-images {
      position: sticky;
      top: 50vh;
      height: 400px;
      margin: 0 auto;
      overflow: hidden;
      z-index: -1;
    }
  
    .projects-section {
      position: relative;
      background-color: rgb(249 250 251);
      padding: 2rem 0;
      /*margin-top: 70vh;*/
      z-index: 1;
    }
  
    .font-simple {
      font-family: Simple-Regular;
    }
  
    /* Regular heading styles */
    h1 {
      font-size: 1.25rem; /* 20px */
      line-height: 1.5;
      font-weight: normal;
    }
  
    h2 {
      font-size: 1.5rem; /* 24px */
      line-height: 1.4;
      font-weight: normal;
    }
  
    h3 {
      font-size: 1.125rem; /* 18px */
      line-height: 1.5;
      font-weight: normal;
    }
  
    h4 {
      font-size: 1rem; /* 16px */
      line-height: 1.5;
      font-weight: normal;
    }
  
    h5 {
      font-size: 0.875rem; /* 14px */
      line-height: 1.5;
      font-weight: normal;
    }
  
    /* Override for the large animated headings */
    h2.text-\[300px\] {
      font-size: 150px; /* Default size for mobile */
      line-height: 1;
      font-weight: 300;
      
      @media (min-width: 768px) {
        font-size: 300px; /* Larger size for desktop */
      }
    }
  
    .project-title a {
      text-decoration: none;
      overflow: hidden;
      position: relative;
      border-bottom: 1px solid #000;
      transition: border-color 0.3s ease-out;
    }
  
    .project-title a:hover {
      border-color: #F96604;
    }
  
    .link-text {
      display: block;
      overflow: hidden;
    }
  
    .link-underline {
      position: relative;
    }
  
    .link-underline::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 1px;
      bottom: 0;
      left: 0;
      background-color: #F96604;
      transform: scaleX(0);
      transform-origin: bottom right;
      transition: transform 0.3s ease-out;
    }
  
    .link-underline:hover::after {
      transform: scaleX(1);
      transform-origin: bottom left;
    }
  
    @media (min-width: 1920px) {
      /* Target the actual container elements in your HTML */
      .w-full.md\:px-8.md\:max-w-7xl.md\:mx-auto {
        max-width: 1600px !important; /* Override the md:max-w-7xl */
        padding: 0 4rem !important;
      }
      
      .text-\[300px\] {
        font-size: 400px; /* Larger font size for Full HD headings */
      }
      
      .gradient-circles {
        width: 400px;  /* Larger circles for Full HD */
        height: 280px;
        margin-left: 20rem;
      }
      
      .circle {
        width: 280px;  /* Larger circle size for Full HD */
        height: 280px;
      }
      
      .circle-blue {
        left: 140px;
        top: 140px;
      }
      
      .blue-word, .orange-word {
        width: 220px;
        font-size: 1.5rem;
      }
      
      .blue-word {
        left: -60px;
      }
      
      .orange-word {
        right: -60px;
        top: 280px;
      }
      
      .splash-images {
        max-width: 800px;
        height: 500px;
      }
      
      h1 {
        font-size: 1.5rem;
      }
      
      h2 {
        font-size: 2rem;
      }
      
      p {
        font-size: 1.125rem;
      }
      
      /* Make project preview images larger */
      .project-title .absolute.w-40 {
        width: 12rem; /* Larger preview images */
      }
      
      /* Increase spacing in the grid */
      .grid-cols-12.gap-4.md\:gap-8 {
        gap: 2rem;
      }
    }
  
    /* Container styles */
    :global(.container) {
      width: 100%;
      margin-left: auto;
      margin-right: auto;
      padding-left: 0.5rem;  /* 8px for mobile */
      padding-right: 0.5rem;
    }
  
    @media (min-width: 640px) {
      :global(.container) {
        padding-left: 1rem;   /* 16px for sm */
        padding-right: 1rem;
      }
    }
  
    @media (min-width: 768px) {
      :global(.container) {
        padding-left: 2rem;   /* 32px for md */
        padding-right: 2rem;
      }
    }
  
    @media (min-width: 1024px) {
      :global(.container) {
        padding-left: 4rem;   /* 64px for lg */
        padding-right: 4rem;
      }
    }
  
    @media (min-width: 1280px) {
      :global(.container) {
        padding-left: 6rem;   /* 96px for xl */
        padding-right: 6rem;
      }
    }
  
    @media (min-width: 1536px) {
      :global(.container) {
        padding-left: 8rem;   /* 128px for 2xl */
        padding-right: 8rem;
      }
    }
  
    /* Add these new styles for slow rotation */
    .slow-rotate {
      animation: slowRotate 60s linear infinite;
      animation-play-state: running !important;
    }
    
    .slow-rotate-reverse {
      animation: slowRotateReverse 60s linear infinite;
      animation-play-state: running !important;
    }
    
    @keyframes slowRotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    @keyframes slowRotateReverse {
      from { transform: rotate(0deg); }
      to { transform: rotate(-360deg); }
    }
  
    .image-shadow {
      box-shadow: 0px 224px 63px 0px rgba(0, 0, 0, 0.00), 0px 143px 57px 0px rgba(0, 0, 0, 0.03), 0px 81px 48px 0px rgba(0, 0, 0, 0.10), 0px 36px 36px 0px rgba(0, 0, 0, 0.17), 0px 9px 20px 0px rgba(0, 0, 0, 0.20);
    }
  
    .animate-image-in {
      animation: fadeInUp 0.3s ease-out forwards;
    }
  
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translate(-50%, calc(-50% + 40px));
      }
      to {
        opacity: 1;
        transform: translate(-50%, -50%);
      }
    }
  
    @media (max-width: 640px) {
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    }
  
    .project-hover-image {
      z-index: 100;  /* Ensure hover images are above everything */
    }
  
    .project-item {
      position: relative;
      cursor: pointer;
    }
  
    .project-item:hover h2 {
      color: black;
    }
  
    .project-image {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 40%;
      z-index: -1;
    }
  
    .fade-in-on-view {
      opacity: 0;
      transform: translateY(20px);
      filter: blur(8px);
      transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s, filter 0.8s;
    }
  
    .fade-in-on-view.in-view {
      opacity: 1;
      transform: translateY(0);
      filter: blur(0px);
    }
  
    /* Remove the initial transform for work text */
    h2.text-\[300px\].whitespace-nowrap {
      transform: none;
      transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }
  
    /* Add rotation styles */
    .rotate-90 {
      transform: rotate(90deg);
    }
  
    /* Remove or comment out the local .tag and .tag-selected styles to use global styles */
    /*
    .tag {
      background-color: whitesmoke;
      color: black;
      padding: 0.25rem 0.5rem;
      display: inline-block;
      margin-right: 0.5rem;
      margin-bottom: 0.25rem;
      margin-top: 0.25rem;
      font-size: 0.875rem;
      transition: background-color 0.3s ease, color 0.3s ease;
      cursor: pointer;
      border-bottom: 1px solid #ccc;
    }

    .tag:hover {
      background-color: black;
      color: white;
    }

    .tag-selected {
      background-color: black;
      color: white;
    }
    */
  
    .tag-health {
      background-color: #1F3A93; /* Example shade for Health */
    }
  
    .tag-culture {
      background-color: #3498DB; /* Example shade for Culture */
    }
  
    .tag-transportation {
      background-color: #9B59B6; /* Example shade for Transportation */
    }
  
    .tag-fintech {
      background-color: #E74C3C; /* Example shade for Fintech */
    }
  
    .tag-emerging-tech {
      background-color: #F39C12; /* Example shade for Emerging Tech */
    }
  
    /* Add styles for the circular badge */
    .badge {
      display: inline-block;
      background-color: #1F3A93; /* Use the same blue color for consistency */
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      line-height: 24px;
      text-align: center;
      font-size: 0.75rem;
      margin-left: 0.5rem;
    }
  
    .rotating-words {
      display: inline-flex;
      position: relative;
      height: 1.2em;
      vertical-align: top;
      min-width: 120px;
    }

    .word {
      position: absolute;
      animation: cycle-blur 9s linear infinite;
      animation-delay: calc(var(--i) * -3s);
      opacity: 0;
      filter: blur(8px);
      width: 100%;
      transition: opacity 0.4s, filter 0.4s;
    }

    @keyframes cycle-blur {
      0%, 2% {
        opacity: 0;
        filter: blur(8px);
      }
      5%, 30% {
        opacity: 1;
        filter: blur(0px);
      }
      33%, 100% {
        opacity: 0;
        filter: blur(8px);
      }
    }

    .word:nth-child(1) { --i: 0; }
    .word:nth-child(2) { --i: 1; }
    .word:nth-child(3) { --i: 2; }
  
    h2.text-\[300px\].whitespace-nowrap {
      will-change: transform;
    }
  
    .work-marquee-container {
      width: 100vw;
      overflow: hidden;
      height: 150px;
      position: relative;
      margin-bottom: 4rem;
      display: flex;
      align-items: center;
    }
    @media (min-width: 768px) {
      .work-marquee-container {
        height: 300px;
      }
    }
    .work-marquee {
      display: flex;
      align-items: center;
      white-space: nowrap;
      will-change: transform;
      height: 100%;
      transform: translate3d(0, 0, 0);
    }
    .work-word {
      font-size: 150px;
      font-family: Simple-Regular, system-ui, sans-serif;
      margin-right: 2vw;
      color: black;
      font-weight: 300;
      line-height: 1;
      user-select: none;
      pointer-events: none;
    }
    @media (min-width: 768px) {
      .work-word {
        font-size: 300px;
      }
    }

    .animate-on-view {
      opacity: 0;
      transform: translateY(20px);
      filter: blur(8px);
      transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s, filter 0.8s;
    }
    .animate-on-view.in-view {
      opacity: 1;
      transform: translateY(0);
      filter: blur(0px);
    }

    .logo-carousel {
      transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .logo-img {
      transition: filter 0.5s, opacity 0.5s;
      opacity: 0.5;
      filter: blur(2.5px) grayscale(60%);
    }
    .logo-center {
      opacity: 1;
      filter: none;
      z-index: 1;
    }
    .logo-blur {
      opacity: 0.5;
      filter: blur(2.5px) grayscale(60%);
      z-index: 0;
    }
  </style>
  


  